/**
 * The wait, and what fills it.
 *
 * A draw takes half a minute for a machine and four for a drawn character, and
 * this is the whole of what somebody looks at while it happens: the model
 * working out what to draw, then the sprite building itself part by part or row
 * by row, with the parts stacking up in a list beside it.
 *
 * Its own file because it is a self-contained five hundred lines that the
 * workbench only ever asks four things of: open, close, say it failed, and say
 * it is stuck. It reaches out for nothing but the shared drawing helpers, which
 * is what made it liftable out of a file that had grown to two and a half
 * thousand lines and was carrying this as one of twenty sections.
 */

import { bitmapOf } from "../src/render.js";
import { fitScale } from "../src/raster.js";
import { SpecBuilder } from "../src/stream.js";
import { KITS } from "../src/kit.js";
import { css } from "./colour.js";
import { blit } from "./paint.js";
import type { DrawWatch } from "./service.js";

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

const veil = $<HTMLDivElement>("veil");
const veilQ = $<HTMLHeadingElement>("veil-h");
const veilStep = $<HTMLParagraphElement>("veil-step");
const veilClose = $<HTMLButtonElement>("veil-close");
const veilEyebrow = $("veil-eyebrow");
const veilFoot = $("veil-foot");
const veilCard = veilClose.closest(".veil-card");
const veilCanvas = $<HTMLCanvasElement>("veil-canvas");
const veilWren = $<HTMLImageElement>("veil-wren");
const veilKit = $<HTMLDivElement>("veil-kit");
const veilTally = $<HTMLDivElement>("veil-tally");
const veilLog = $<HTMLOListElement>("veil-log");
const veilSidehead = $<HTMLParagraphElement>("veil-sidehead");

/**
 * What the wait is actually spent on, roughly when it is spent.
 *
 * These are honest about the order of work rather than a spinner pretending to
 * be a percentage. The last one carries no promise, because a slow request is
 * still working and saying "nearly there" forever would be a lie.
 */
const STEPS: [number, string][] = [
	[0, "Reading your description"],
	[4, "Working out the shape and the size"],
	[11, "Choosing a palette"],
	[19, "Writing the parts, in order"],
	[32, "Drawing it pixel by pixel"],
	[55, "Checking that it draws"],
	[75, "Still going. A drawn subject takes longer than an assembled one"],
	// Time is all these know, and they are now only the fallback. The draw reports
	// what it is doing, so `liveStep` says how many parts have arrived or which
	// row is being painted, and a second attempt says so because it is told.
	// These cover the stretch before the model has written anything at all, which
	// at medium effort is most of the first minute.
	[120, "Still going. A detailed subject can take a couple of minutes"],
];

let veilTimer = 0;
let veilStart = 0;
let lastFocus: HTMLElement | null = null;

// ---- the sprite, while it is still being written ------------------------------------

/**
 * The wait, spent watching the thing you asked for appear.
 *
 * The spec arrives in an order that can be drawn as it comes: the canvas and
 * the palette first, then one finished part at a time, then the rows of a
 * drawing one line at a time. So the overlay draws it. A composed sprite
 * assembles in the order the model chose, casing then panel then lamp, and a
 * drawn one prints from the top down.
 *
 * This is honest in the way the meter below it cannot be. Those are the real
 * pixels of the real sprite, arriving when they arrive.
 */
const building = new SpecBuilder();

/** What has arrived, so the status line can stop guessing at it. */
let built = { parts: 0, rows: 0, restarted: false, drawn: false, kitted: false, thought: 0 };
let repaint = 0;
let restarting = false;

/** The stage the sprite is drawn on, in screen pixels. */
const VEIL_BOX = 288;

/**
 * How long freshly drawn pixels stay lit before settling.
 *
 * Long enough to see one part land, since a composed sprite arrives a part
 * every second or so and a shorter flash is a blink you miss while looking at
 * the wrong corner. Rows arrive far faster than this and their flashes overlap,
 * which is fine: only the newest is drawn, so a run of rows reads as a sweep
 * down the sprite rather than as a stack of flashes.
 */
const FLASH_MS = 420;

/**
 * The pixels that arrived last, and when, so they can cool off.
 *
 * Held as a mask of exactly what changed rather than as a row or a rectangle.
 * A drawn sprite arrives a row at a time and a composed one a part at a time,
 * and those look nothing alike: there is no row frontier on a cupboard, and a
 * head sweeping down one would be describing something that is not happening.
 *
 * Diffing what is on screen against what was there a moment ago covers both
 * without knowing which it is, and it is exact. A part that lands mostly behind
 * another lights only the sliver of it you can actually see, which is the truth
 * about what that part contributed.
 */
let flash: { mask: Uint8Array; at: number } | null = null;
/** The last frame painted, to compare the next one against. */
let painted: Uint8Array | null = null;
/** Something new has arrived, so the next paint is worth diffing. */
let fresh = false;

/**
 * The palette, laid out before a pixel of the sprite exists.
 *
 * A real beat that nothing showed. The kit is written before the parts, so at
 * the moment the canvas is still empty every colour the sprite will ever use is
 * already decided, and putting them on screen gives the first stretch of the
 * wait something true to look at.
 *
 * Fully transparent roles are left out. `none` is a real role and drawing it
 * would be a swatch of nothing beside the colours that mean something.
 */
function showKit(kit: unknown) {
	/*
	 * A kit is usually written out in full, and occasionally named.
	 *
	 * The model always writes one inline, because a name would refer to a set of
	 * roles it was never shown. A spec opened from this repository names one, and
	 * resolving it here costs a lookup and means the palette is right for both.
	 */
	const resolved = typeof kit === "string" ? KITS[kit] : kit;
	const roles = (resolved as { roles?: Record<string, number[]> } | null)?.roles;
	if (!roles) return;
	const swatches = Object.values(roles)
		.filter((rgba) => Array.isArray(rgba) && (rgba[3] ?? 255) > 0)
		.map((rgba, i) => {
			const cell = document.createElement("span");
			cell.style.background = css(rgba);
			// Staggered so the palette lands as a run rather than all at once.
			cell.style.animationDelay = `${Math.min(i * 40, 800)}ms`;
			return cell;
		});
	veilKit.replaceChildren(...swatches);
}

/**
 * The line the model is writing, drawn across the sprite.
 *
 * Only ever where the drawing has actually reached, which is what makes it
 * worth having: it is the one mark on this card that is not an estimate. It
 * disappears between parts and when the drawing closes, because at that point
 * there is nothing being written and a head parked on a finished sprite would
 * be claiming otherwise.
 */
function drawHead(target: HTMLCanvasElement, row: number, scale: number) {
	const ctx = target.getContext("2d");
	if (!ctx) return;
	const y = row * scale;
	if (y < 1 || y > target.height) return;

	// The light the head throws up the page. A gradient over a few rows, which
	// is the difference between a line sitting on a sprite and a machine drawing
	// one.
	const glow = ctx.createLinearGradient(0, y - scale * 6, 0, y);
	glow.addColorStop(0, "rgba(122, 226, 255, 0)");
	glow.addColorStop(1, "rgba(122, 226, 255, 0.22)");
	ctx.fillStyle = glow;
	ctx.fillRect(0, y - scale * 6, target.width, scale * 6);

	ctx.fillStyle = "rgba(150, 235, 255, 0.9)";
	ctx.fillRect(0, y - Math.max(1, Math.round(scale / 4)), target.width, Math.max(1, Math.round(scale / 4)));
}

/**
 * Which pixels are not what they were, or null if none of them changed.
 *
 * One byte per pixel rather than a list of them, because it is read back once
 * per frame while it fades and a mask is the cheaper thing to walk.
 */
function changedPixels(before: Uint8Array, after: Uint8Array): Uint8Array | null {
	const mask = new Uint8Array(after.length / 4);
	let any = false;
	for (let i = 0; i < mask.length; i++) {
		const at = i * 4;
		if (
			before[at] !== after[at] ||
			before[at + 1] !== after[at + 1] ||
			before[at + 2] !== after[at + 2] ||
			before[at + 3] !== after[at + 3]
		) {
			mask[i] = 1;
			any = true;
		}
	}
	return any ? mask : null;
}

/**
 * Light up the pixels that just arrived, and let them cool.
 *
 * White rather than a tint, so a pale sprite and a dark one both show it and
 * neither has its colours pulled about. Drawn through an offscreen canvas at
 * sprite resolution so the highlight lands on exactly the pixels it belongs to,
 * with the same hard edges as everything else here.
 */
function lightUp(
	target: HTMLCanvasElement,
	mask: Uint8Array,
	width: number,
	height: number,
	scale: number,
	alpha: number,
) {
	const ctx = target.getContext("2d");
	if (!ctx) return;
	const off = new OffscreenCanvas(width, height);
	const octx = off.getContext("2d");
	if (!octx) return;
	const lit = octx.createImageData(width, height);
	for (let i = 0; i < mask.length; i++) {
		if (!mask[i]) continue;
		lit.data[i * 4] = 255;
		lit.data[i * 4 + 1] = 255;
		lit.data[i * 4 + 2] = 255;
		lit.data[i * 4 + 3] = 255;
	}
	octx.putImageData(lit, 0, 0);
	ctx.save();
	ctx.globalAlpha = alpha;
	ctx.imageSmoothingEnabled = false;
	ctx.drawImage(off, 0, 0, width * scale, height * scale);
	ctx.restore();
}

/**
 * One line in the build log: a part, in the order it is drawn.
 *
 * A drawing is logged when it starts rather than when it closes, because it is
 * one part that takes minutes to write and a log that waited would be empty for
 * the whole of it. Marked open until the part actually lands.
 */
function logPart(index: number, shape: string, open: boolean) {
	const at = `${index + 1}`.padStart(2, "0");
	const found = veilLog.querySelector<HTMLLIElement>(`li[data-at="${index}"]`);
	const line = found ?? document.createElement("li");
	line.dataset.at = String(index);
	if (open) line.dataset.open = "1";
	else delete line.dataset.open;
	line.replaceChildren(Object.assign(document.createElement("span"), { textContent: at }), document.createTextNode(shape));
	if (!found) veilLog.append(line);
	// Only ever a dozen visible, and the newest is the one worth seeing.
	while (veilLog.children.length > 14) veilLog.firstElementChild?.remove();
}

function paintBuild() {
	repaint = 0;
	const spec = building.spec;
	if (!spec) return;
	let bitmap;
	try {
		// One frame. An animated sprite is drawn as its first frame while it
		// arrives, because a preview that starts animating halfway through is two
		// things moving at once and neither reads.
		bitmap = bitmapOf(spec);
	} catch {
		/*
		 * A state that will not draw is skipped, not reported.
		 *
		 * The builder holds nothing back that it knows to be incomplete, but the
		 * model is free to write a part that refers to a role it has not defined
		 * yet, and that is not an error, it is a sentence half written. The next
		 * part almost always fixes it, and the finished spec is validated properly
		 * by the code that decides whether the draw succeeded.
		 */
		return;
	}
	// A whole-number scale or none. Half a pixel is the one thing this whole
	// project argues against.
	const scale = fitScale(bitmap.width, bitmap.height, VEIL_BOX);
	blit(veilCanvas, bitmap, scale);

	/*
	 * Only diff when something actually arrived.
	 *
	 * This runs again on its own while a flash fades, and nothing has changed on
	 * those passes. Diffing them would find no pixels and throw away the flash
	 * that is still fading.
	 */
	if (fresh) {
		fresh = false;
		if (painted?.length === bitmap.data.length) {
			const mask = changedPixels(painted, bitmap.data);
			if (mask) flash = { mask, at: performance.now() };
		}
		painted = bitmap.data.slice();
	}

	const age = flash ? performance.now() - flash.at : Infinity;
	if (flash && age < FLASH_MS) {
		lightUp(veilCanvas, flash.mask, bitmap.width, bitmap.height, scale, 0.55 * (1 - age / FLASH_MS));
	}
	// The head belongs to a drawing and to nothing else, so it appears only while
	// rows are being written and never on a sprite built out of parts.
	const row = building.frontier;
	if (row !== null) drawHead(veilCanvas, row, scale);
	veilCanvas.hidden = false;
	veilWren.hidden = true;
	built.drawn = true;
	// Faded out by a restart, and brought back only once there is something to
	// come back to. Timing it with a delay instead would sometimes fade an empty
	// canvas in and then fill it, which looks like the fault it is meant to hide.
	if (restarting) {
		restarting = false;
		veilCanvas.removeAttribute("data-state");
	}
	// A flash has to cool off on its own, since no event is coming to do it. The
	// loop stops as soon as the last one has faded.
	if (age < FLASH_MS) repaint ||= requestAnimationFrame(paintBuild);
}

/**
 * Each piece of the sprite, as the service reports it.
 *
 * Painting is put off to the next frame rather than done here. Rows arrive in
 * bursts of a dozen, and drawing the whole sprite once per row would spend the
 * wait re-rendering rather than showing anything.
 */
/** One mark per this many characters worked out. */
const TALLY_STEP = 400;
/** Enough marks to fill the width twice over, past which the number is the point. */
const TALLY_MAX = 96;

export function watchDraw(event: DrawWatch) {
	if (event.type === "thinking") {
		/*
		 * The model is working and has written nothing yet.
		 *
		 * A count of characters, never the characters. What it is thinking reasons
		 * about the format out loud, and that is the one thing the failure path was
		 * scrubbed of so an error could not publish it.
		 */
		built.thought = event.chars;
		const want = Math.min(TALLY_MAX, Math.floor(event.chars / TALLY_STEP));
		for (let i = veilTally.children.length; i < want; i++) {
			veilTally.append(document.createElement("span"));
		}
		return;
	}
	if (event.type === "restart") {
		/*
		 * The attempt on screen is being abandoned.
		 *
		 * The canvas empties on purpose and says so, because a sprite that
		 * disappears two minutes into a wait is otherwise indistinguishable from
		 * the page breaking. It stays empty until the second attempt has something
		 * to show, which is the honest length of that pause.
		 */
		building.clear();
		built = { ...built, parts: 0, rows: 0, restarted: true };
		restarting = true;
		// The palette goes with it. The second attempt writes its own kit, and
		// leaving the first one up would show colours the new sprite may not use.
		veilKit.replaceChildren();
		veilLog.replaceChildren();
		veilTally.replaceChildren();
		flash = null;
		painted = null;
		fresh = false;
		veilCanvas.dataset.state = "restarting";
		veilCanvas.getContext("2d")?.clearRect(0, 0, veilCanvas.width, veilCanvas.height);
		return;
	}
	// The first thing the model writes opens the bench beside the stage. Until
	// then the card is Wren and the sprite-to-be, and nothing else.
	if (veilCard?.getAttribute("data-phase") !== "drawing") {
		veilCard?.setAttribute("data-phase", "drawing");
	}

	if (event.type === "meta") {
		built.kitted = true;
		showKit((event.head as { kit?: unknown }).kit);
	}
	if (event.type === "drawing") logPart(event.index, String(event.head.shape ?? "art"), true);
	if (event.type === "part") {
		built.parts += 1;
		logPart(event.index, String(event.part.shape ?? "part"), false);
	}
	if (event.type === "row") built.rows += 1;
	building.take(event);
	fresh = true;
	repaint ||= requestAnimationFrame(paintBuild);
}

/**
 * What is happening, when the page actually knows.
 *
 * Everything below this is timed guesswork, and it was the only thing available
 * before the draw streamed. Now the parts and the rows are counted as they
 * arrive, so the line can say a true thing instead of a likely one.
 */
function liveStep(): string | null {
	if (built.restarted && !built.parts && !built.rows) return "That one broke a rule. Drawing it again from the start";
	if (built.rows) return `Painting it in, row ${built.rows}`;
	if (built.parts === 1) return "Drawing the first part";
	if (built.parts) return `${built.parts} parts drawn`;
	// The palette is on screen by now, so the timed guess underneath would be
	// describing a stage that has visibly already happened.
	if (built.kitted) return "Palette set. Writing the parts";
	// Not a guess: the model is reporting how much it has worked out, and the
	// tally beside this is that same number.
	if (built.thought) return "Working out the shape, the size and the palette";
	return null;
}

/**
 * The one line of status, and no meter under it.
 *
 * There was a meter, and it was a guess: a bar creeping towards a time this
 * could not know, beside a sprite that was telling the truth. Two progress
 * indicators where one is invented is worse than either alone, and the invented
 * one was the bigger of the two.
 */
function paintVeil() {
	const elapsed = performance.now() - veilStart;
	const secs = elapsed / 1000;
	const step = liveStep() ?? [...STEPS].reverse().find(([at]) => secs >= at)?.[1];
	if (step) veilStep.textContent = `${step}… ${Math.floor(secs)}s`;
}

export function openVeil(want: string) {
	lastFocus = document.activeElement as HTMLElement | null;
	veilQ.textContent = want;
	veilClose.hidden = true;
	veil.hidden = false;
	/*
	 * Whatever the last attempt ended as, this one starts as a draw in progress.
	 *
	 * Every part of the stuck state has to be undone here, not just the button.
	 * A card that kept its "Out of credits" heading or its hidden meter while
	 * genuinely drawing would be the same failure pointing the other way.
	 */
	veilCard?.removeAttribute("data-state");
	if (veilEyebrow) veilEyebrow.textContent = "Drawing";
	if (veilFoot) veilFoot.hidden = false;
	veilClose.textContent = "Close";
	veilClose.parentElement?.querySelector(".veil-fix")?.remove();
	/*
	 * Nothing of the last draw survives into this one.
	 *
	 * The overlay is reused, so a sprite left on the canvas would sit there
	 * looking like the beginning of the one just asked for. Wren comes back
	 * because the canvas has nothing on it yet and will not have for a while.
	 */
	building.clear();
	built = { parts: 0, rows: 0, restarted: false, drawn: false, kitted: false, thought: 0 };
	restarting = false;
	veilCanvas.removeAttribute("data-state");
	veilCanvas.hidden = true;
	veilWren.hidden = false;
	veilKit.replaceChildren();
	veilLog.replaceChildren();
	veilTally.replaceChildren();
	veilCard?.setAttribute("data-phase", "thinking");
	flash = null;
	painted = null;
	fresh = false;
	veilStart = performance.now();
	paintVeil();
	veilTimer = window.setInterval(paintVeil, 250);
	veilClose.focus();
}

export function closeVeil() {
	window.clearInterval(veilTimer);
	veil.hidden = true;
	lastFocus?.focus();
}

/** On failure the overlay stays up holding the reason, with a way out. */
export function veilFailed(why: string) {
	window.clearInterval(veilTimer);
	veilStep.textContent = why;
	veilClose.hidden = false;
	veilClose.focus();
}

/**
 * Stopped for a reason you can do something about.
 *
 * The same panel as a failure, plus the way out. The link is a real link rather
 * than a button that opens checkout here, because signing in and topping up are
 * both whole pages with their own state, and a half-modal version of either is
 * how somebody ends up paying twice.
 */
export function veilStuck(title: string, why: string, label: string, href: string) {
	// Reached either from a draw that has already opened this, or straight from a
	// page that can see the answer without asking. Opening it here means the
	// caller does not have to know which, and does not have to reach in and set
	// the overlay's own state to find out.
	if (veil.hidden) {
		lastFocus = document.activeElement as HTMLElement | null;
		veil.hidden = false;
	}
	window.clearInterval(veilTimer);

	/*
	 * Stop looking like work in progress.
	 *
	 * Changing only the status line was not enough and was arguably worse than
	 * saying nothing: the card still said "Drawing", still showed a meter, and
	 * still explained that it plans the whole sprite before writing a part. Every
	 * signal said wait, and the one sentence that said otherwise was the smallest
	 * text on it. Somebody would sit and watch a bar that was never going to move.
	 *
	 * So the whole card changes state. The meter goes rather than emptying,
	 * because an empty meter is still a meter. The heading says what happened
	 * instead of what was asked for. And the way out is the thing in focus.
	 */
	veilCard?.setAttribute("data-state", "stuck");
	if (veilEyebrow) veilEyebrow.textContent = title;
	veilQ.textContent = title;
	veilStep.textContent = why;
	if (veilFoot) veilFoot.hidden = true;

	const body = veilClose.parentElement;
	const existing = body?.querySelector<HTMLAnchorElement>(".veil-fix") ?? null;
	const fix = existing ?? document.createElement("a");
	fix.className = "go small veil-fix";
	fix.href = href;
	fix.textContent = label;
	if (!existing) veilClose.before(fix);

	veilClose.hidden = false;
	veilClose.textContent = "Not now";
	fix.focus();
}

veilClose.addEventListener("click", closeVeil);
// Nothing else is reachable while it is up, so the tab must not escape it either.
veil.addEventListener("keydown", (e) => {
	if (e.key === "Escape" && !veilClose.hidden) closeVeil();
	if (e.key === "Tab") e.preventDefault();
});

/**
 * Did this draw arrive as something to watch, rather than all at once?
 *
 * The workbench replays a sprite part by part when one is opened from a file,
 * which is the right introduction to a spec. After the overlay has just shown
 * the same sprite building itself, it is the same show twice.
 */
export const veilDrew = () => built.drawn;
